import React, { Component, ReactNode } from 'react';
import { ConfirmPopup } from 'primereact/confirmpopup';
import CustomTable, { CustomColumnProps } from '../custom/CustomTable';
import FolderView from '../folder/view/FolderView';
import { DataTableRowClickEventParams } from 'primereact/datatable';
import LanguageUtil from '../../util/LanguageUtil';
import ItemStatusComponent from '../ItemStatusComponent';
import { StreamJsonModel } from '../../domain/stream/stream/StreamDomain';
import StreamService from '../../service/StreamService';
import ServiceContainer from '../../config/ServiceContainer';
import { ServiceNames } from '../../constant/ServiceNames';
import RoleHelper from '../../RoleHelper';
import ListDraggableItem from '../custom/ListDraggableItem';
import { dispatch, RootState } from '../../redux/store';
import { ComponentState, setSelectedItems } from '../../redux/actions/ComponentActions';
import { connect, ConnectedProps } from 'react-redux';
import EmptyComponent from '../custom/empty-components/EmptyComponent';
import { customConfirm } from '../custom/CustomPopupConfirm';
import { confirmUndeployWithImpact } from '../deploy/UndeployImpactDialog';
import Icon from '../../icons/Icon';
import { query } from '@trlogic/client-js';
import { Tooltip } from 'primereact/tooltip';
import ComponentName from '../../constant/ComponentName';
import { PermissionType } from '../../domain/rbac/RbacDomains';
import CustomTableContentWrapper from '../custom/CustomTableContentWrapper';
import ComponentUtil from '../../util/ComponentUtil';
import moment from 'moment/moment';
import Order = query.Order;

class StreamListViewerComponent extends Component<Props, State> {
  public state = new State();

  private readonly streamService: StreamService = ServiceContainer.getInstance().get(ServiceNames.STREAM);

  public render(): ReactNode {
    const {
      views,
      streams,
      component: { selectedComponentIds },
      onSortChange,
    } = this.props;
    return views && views.length > 0 && streams && streams.length > 0 ? (
      <div
        className={
          'overflow-y-scroll max-h-[calc(100dvh-430px)] scrollable-editor-container scrollable-editor-container 2xl:max-h-[calc(100dvh-460px)] flex w-full  rounded-lg'
        }
      >
        <ConfirmPopup />
        <CustomTable
          value={streams}
          selectionMode='checkbox'
          rowClassName={(data) => {
            const view = views.find((v: FolderView) => v.getModel().value?.payload.name == data.name);
            return view ? `list_${view?.getModel().id}` : '';
          }}
          componentName={ComponentName.STREAM}
          openNewTab={true}
          onRowClick={this.openEditor}
          selection={selectedComponentIds}
          onSelectionChange={this.onSelectionChange}
          columns={this.getColumns()}
          onSortChange={onSortChange}
        />
        <ListDraggableItem itemType={ComponentName.STREAM} currentModal={this.props.currentModal} />
      </div>
    ) : (
      <EmptyComponent type={ComponentName.STREAM} />
    );
  }

  public componentDidMount() {
    const { streams } = this.props;
    this.setState({ streams, loading: false });
  }

  private openEditor = (e: DataTableRowClickEventParams) => {
    const { openStream } = this.props;
    const name = e.data.name;
    openStream(name);
  };

  private onSelectionChange = (e: any) => {
    dispatch(setSelectedItems(e.value));
  };

  private getColumns = (): CustomColumnProps[] => {
    const columns: CustomColumnProps[] = [
      {
        field: 'name',
        header: LanguageUtil.get({ id: `pages.stream.table.name`, defaultMessage: 'Name' }),
        className: 'min-w-[16%]',
        copyable: true,
        onCopy: (e: StreamJsonModel) => {
          return e.name;
        },
        body: (record: StreamJsonModel) => {
          const { views } = this.props;
          const view: FolderView = views.find((view: FolderView) => view.getModel().value?.payload.name == record.name);
          return view ? (
            <div className={'flex items-start text-xs 2xl:text-md'} id={``}>
              <div id={`list_${view?.getModel().id}`} />
              <CustomTableContentWrapper
                clipSize={25}
                showEllipsis={true}
                text={record.name}
                data-testid={`${ComponentName.STREAM}_name_text_${record.name}`}
              />
            </div>
          ) : null;
        },
      },
      {
        field: 'stage',
        header: LanguageUtil.get({ id: `pages.stream.table.status`, defaultMessage: 'Status' }),
        className: 'min-w-[15%]',
        body: (record: StreamJsonModel) => {
          return (
            <ItemStatusComponent status={record.stage} containerClassName={'justify-start'} data-testid={`${ComponentName.STREAM}_stage_text_${record.name}`} />
          );
        },
      },
      {
        field: 'description',
        header: LanguageUtil.get({ id: `pages.stream.table.description`, defaultMessage: 'Description' }),
        className: 'min-w-[20%]',
        body: (record: StreamJsonModel) => {
          return (
            <CustomTableContentWrapper
              clipSize={30}
              showEllipsis={true}
              text={record.description || LanguageUtil.get({ id: 'common.label.noDescription', defaultMessage: 'No description' })}
              data-testid={`${ComponentName.STREAM}_description_text_${record.name}`} />
          );
        },
      },
      {
        field: 'transaction',
        header: LanguageUtil.get({ id: `pages.stream.table.transaction`, defaultMessage: 'Transaction' }),
        className: 'min-w-[15%]',
        body: (record: StreamJsonModel) => {
          return (
            <div className={'flex w-full items-start text-f-black-3 text-xs 2xl:text-md justify-start relative'}>
              <CustomTableContentWrapper
                clipSize={25}
                showEllipsis={true}
                text={record.transaction || 'No transaction'}
                data-testid={`${ComponentName.STREAM}_channel_text_${record.name}`} />
            </div>
          );
        },
      },
      {
        field: 'createdAt',
        header: LanguageUtil.get({ id: `pages.stream.table.createdAt`, defaultMessage: 'Created At' }),
        className: 'min-w-[12%]',
        body: (record: StreamJsonModel) => {
          return (
            <div className={'flex w-full items-start text-f-black-3 text-xs 2xl:text-md justify-start relative group'}>
              <CustomTableContentWrapper text={record.createdAt ? moment(record.createdAt).format('LLL') : ''} />
            </div>
          );
        },
      },
      {
        field: 'updatedAt',
        header: LanguageUtil.get({ id: `pages.stream.table.updatedAt`, defaultMessage: 'Updated At' }),
        className: 'min-w-[12%]',
        body: (record: StreamJsonModel) => {
          return (
            <div className={'flex w-full items-start text-f-black-3 text-xs 2xl:text-md justify-start relative group'}>
              <CustomTableContentWrapper text={record.updatedAt ? moment(record.updatedAt).format('LLL') : ''} />
            </div>
          );
        },
      },
    ];
    if (RoleHelper.canEdit('Stream')) {
      columns.push({
        field: 'action',
        header: 'Action',
        className: 'max-w-[75px] 2xl:max-w-[100px]',
        sortable: false,
        body: this.getActions,
      });
    }
    return columns;
  };

  private getActions = (record: StreamJsonModel) => {
    const { permissions } = this.props;

    const actions: ReactNode[] = [];

    const permission = permissions[record.name];

    const allowed = ['Edit', 'Admin'];
    if (!allowed.includes(permission)) {
      return null;
    }
    const isLocked = ComponentUtil.isLocked(ComponentName.STREAM, record.name);
    if (isLocked) {
      return null;
    }

    const canRemove = record.stage == 'DRAFT';
    const canUndeploy = record.stage != 'DRAFT';

    if (canUndeploy) {
      actions.push(
        <div
          key='undeploy'
          onClick={(e: any) => {
            e.stopPropagation();
            e.preventDefault();
            customConfirm({
              target: e.currentTarget,
              accept: async () => confirmUndeployWithImpact(ComponentName.STREAM, [record.name], () => this.streamService.undeploy(record.name)),
              message: LanguageUtil.get({ id: 'messages.confirm.undeploy1', defaultMessage: 'Are you sure you want to undeploy?' }),
            });
          }}
          data-testid={`${ComponentName.STREAM}_undeploy_button_${record.name}`}
        >
          <Tooltip position={'left'} target={'.undeploy'}>
            {LanguageUtil.get({ id: 'common.label.undeploy', defaultMessage: 'Undeploy' })}
          </Tooltip>
          <Icon icon={'undeploy'} size={20} className={'undeploy hover:text-primary text-[#607080] hover:cursor-pointer'} />
        </div>,
      );
    }
    if (canRemove) {
      actions.push(
        <div
          key='remove'
          onClick={(e: any) => {
            e.stopPropagation();
            e.preventDefault();
            customConfirm({
              target: e.currentTarget,
              accept: async () => await this.streamService.delete(record.name),
              message: LanguageUtil.get({ id: 'messages.confirm.remove1', defaultMessage: 'Are you sure you want to remove?' }),
            });
          }}
          data-testid={`${ComponentName.STREAM}_remove_button_${record.name}`}
        >
          <Tooltip position={'left'} target={'.remove'}>
            {LanguageUtil.get({ id: 'common.label.remove', defaultMessage: 'Remove' })}
          </Tooltip>
          <Icon
            icon={'delete'}
            size={20}
            color={'primary'}
            className={'remove hover:text-primary text-[#607080] hover:cursor-pointer flex items-center justify-center'}
          />
        </div>,
      );
    }
    actions.push(
      <div
        key='deploy'
        onClick={(e: any) => {
          e.stopPropagation();
          e.preventDefault();
          customConfirm({
            target: e.currentTarget,
            accept: async () => await this.streamService.deploy(record),
            message: LanguageUtil.get({ id: 'messages.confirm.deploy1', defaultMessage: 'Are you sure you want to deploy?' }),
          });
        }}
        data-testid={`${ComponentName.STREAM}_deploy_button_${record.name}`}
      >
        <Tooltip position={'left'} target={'.deploy'}>
          {LanguageUtil.get({ id: 'common.label.deploy', defaultMessage: 'Deploy' })}
        </Tooltip>
        <Icon icon={'deploy'} size={20} className={'deploy hover:text-primary text-[#607080] hover:cursor-pointer'} />
      </div>,
    );

    return <div className={'flex w-full justify-start items-center gap-x-4'}>{actions}</div>;
  };
}

class State {
  streams: StreamJsonModel[] = [];
  loading: boolean = true;
}

interface Props extends ReduxProps {
  views: any;
  streams: StreamJsonModel[];
  openStream: (id: string) => void;
  currentModal: any;
  onSortChange: (sort: Order[]) => void;
  permissions: Record<string, PermissionType>;
}

const mapStateToProps = (state: RootState) => ({
  component: state.component as ComponentState,
});
const mapDispatchToProps = {};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;
export default connector(StreamListViewerComponent);
